/**
 * @file index.tsx
 *
 * @fileoverview Renders the Snowplow tracking script in the page.
 */

import React, { useContext, useEffect, useRef } from 'react';
import { checkUserConsentForService } from '~/jobiqo/services/cmp';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { SERVICES } from '~/jobiqo/lib/cmp';
import Router from 'next/router';
import Script from 'next/script';
import { PathContextInterface } from '~/jobiqo/models/app';
import { PathContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const usePageReloadIfSnowplowConsentGotRemoved = (currentSnowplowConsent) => {
  // If the consent has changed from true to false we need to reload
  // the page otherwise Snowplow does not get excluded.
  const previousSnowplowConsent = useRef<boolean>(null);
  const userContext: UserContextInterface = useContext(UserContext);

  // Save current consent state to compare.
  useEffect(() => {
    previousSnowplowConsent.current = checkUserConsentForService(
      userContext?.consents,
      SERVICES.Snowplow,
    );
  }, [userContext.consents]);

  // Compare if consent has changed from true to false.
  if (previousSnowplowConsent.current && !currentSnowplowConsent) {
    Router.reload();
  }
};

export const Snowplow = () => {
  const userContext: UserContextInterface = useContext(UserContext);
  const pathContext: PathContextInterface = useContext(PathContext);
  const { APP } = useFrontendConfig();
  const {
    APP_ID: SNOWPLOW_APP_ID,
    COLLECTOR_URL: SNOWPLOW_COLLECTOR_URL,
    POST_PATH: SNOWPLOW_POST_PATH,
    ENABLE_TRACKING: SNOWPLOW_ENABLE_TRACKING,
    MINIMUM_VISIT_LENGTH: SNOWPLOW_MINIMUM_VISIT_LENGTH,
    HEARBEAT_DELAY: SNOWPLOW_HEARBEAT_DELAY,
  } = APP.SNOWPLOW;
  const testing = process.env.testing;
  const staging = process.env.staging;

  const isUserConsentNeededAndGiven = checkUserConsentForService(
    userContext?.consents,
    SERVICES.Snowplow,
  );

  useEffect(() => {
    if (!testing && !staging) {
      if (typeof window !== 'undefined' && (window as any)?.snowplow) {
        // @ts-ignore
        snowplow('trackPageView');
      }
    }
  }, [pathContext, isUserConsentNeededAndGiven]);

  usePageReloadIfSnowplowConsentGotRemoved(isUserConsentNeededAndGiven);

  return isUserConsentNeededAndGiven &&
    SNOWPLOW_APP_ID &&
    SNOWPLOW_COLLECTOR_URL &&
    !testing &&
    !staging ? (
    <>
      <Script id="Snowplow-script" type="text/javascript" async={true}>{`
      ;(function(p,l,o,w,i,n,g){if(!p[i]){p.GlobalSnowplowNamespace=p.GlobalSnowplowNamespace||[]; p.GlobalSnowplowNamespace.push(i);p[i]=function(){(p[i].q=p[i].q||[]).push(arguments) };p[i].q=p[i].q||[];n=l.createElement(o);g=l.getElementsByTagName(o)[0];n.async=1; n.src=w;g.parentNode.insertBefore(n,g)}}(window,document,"script","https://cdn.jsdelivr.net/npm/@snowplow/javascript-tracker@3.16.0/dist/sp.min.js","snowplow"));

      snowplow('newTracker', 'sp', '${SNOWPLOW_COLLECTOR_URL}', {
        appId: '${SNOWPLOW_APP_ID}',
        platform: 'web',
        discoverRootDomain: true,
        cookieSameSite: 'Lax', // Recommended
        cookieSecure: true,
        stateStorageStrategy: 'cookieAndLocalStorage',
        ${SNOWPLOW_POST_PATH ? `postPath: '${SNOWPLOW_POST_PATH}',` : ''}
        encodeBase64: true,
        respectDoNotTrack: false,
        contexts: {
          webPage: true,
          session: false,
          browser: false,
          performanceTiming: true,
          gaCookies: true,
          clientHints: true
        }
      });

      ${
        SNOWPLOW_ENABLE_TRACKING
          ? `// Turn on page pings.
      snowplow('enableActivityTracking', {
        minimumVisitLength: ${SNOWPLOW_MINIMUM_VISIT_LENGTH},
        heartbeatDelay: ${SNOWPLOW_HEARBEAT_DELAY}
      });`
          : ''
      }
      `}</Script>
    </>
  ) : null;
};

export default Snowplow;
